/**
 * @module Permission/Directive
 */

import {Directive, ElementRef, Input, Optional, Renderer2, TemplateRef, ViewContainerRef} from '@angular/core';
import { PermissionContextService } from '@app/services/permissions/permission-context.service';
import {PermissionMode, PermissionsObject} from '@app/services/permissions/permission.model';

/**
 * Directive that manages the visibility of elements based on user permissions.
 *
 * Original approach supposed usage of method directly. Promise returned from a method/function
 * causes endless loop in UI. Every time the function runs, it's going to return a new instance of a Promise
 * which is going to resubscribe in the async pipe. When it resolves, it's going to trigger
 * the change detection mechanism which will repeat the same loop.
 * This was solved by creating a Directive and a new EventEmitter variable within it.
 * Subscribing to its change allows us to track the event of the permissions check finished.
 *
 * IMPORTANT
 * When using permissionMode="disable", you should remove the asterisk * from the directive.
 * The * makes it a structural directive which is optimized for adding/removing elements,
 * whereas for disabling, you want it to act as an attribute directive on the existing element.
 *
 * @example
 * Without the *, Angular sees that the ngIf directive is being applied to the div element,
 * but since there is no * or `<template>` tag, it is unable to locate a template, hence the error.
 * <div *hasPermissions="['canViewPayment', 'canCreatePayment']">Yes, you can</div>
 * <div *hasPermissions="'canViewPayment'">Yes, you can</div>
 *
 * <!-- The button stays in DOM but becomes greyed out and unclickable -->
 * <button [hasPermissions]="['DELETE_USER']" permissionMode="disable">Delete</button>
 *
 * @model
 * @@Directive
 *
 * @see [Angular conditional directive](https://github.com/angular/angular/blob/dcfabf08afd888dcc891900c883910ab35efb8eb/packages/common/src/directives/ng_if.ts)
 * @see [AsyncPipe maintains reference of the Strategy](https://github.com/angular/angular/issues/3798)
 * @see [Promise causes endless loop](https://github.com/angular/angular/issues/5187)
 * @see [Wait for click promise to complete](https://stackoverflow.com/questions/54692978/angular-directive-that-waits-for-click-promise-to-complete)
 * @see [Permission and roles based access control](https://github.com/AlexKhymenko/ngx-permissions)
 * @see [No provider for TemplateRef](https://stackoverflow.com/a/35932075/15391077)
 */
@Directive({ selector: '[hasPermissions]' })
export class PermissionDirective {
	/**
	 * The array of permissions associated with the directive.
	 *
	 * @type {Array<string | PermissionsObject>}
	 * @private
	 * @hidden
	 */
	private _permissions: Array<string | PermissionsObject>;

	/**
	 * Permissions mode to maintain backward compatibility.
	 *
	 * @type {PermissionMode}
	 * @private
	 * @hidden
	 */
	private _mode: PermissionMode = PermissionMode.REMOVE;

	/**
	 * Sets the permissions for the directive and manages the visibility of the associated content.
	 *
	 * This setter checks if the provided permissions are null or not. If they are null, it creates
	 * an embedded view of the template. If permissions are provided, it verifies whether the user
	 * has the necessary permissions and updates the view accordingly.
	 *
	 * @param {Array<string | PermissionsObject>} permissions - The permissions to check against.
	 *
	 * @setter
	 *
	 * @see [Hide parts of menu](https://stackoverflow.com/questions/56562873/hide-parts-of-menu-in-angular-permission)
	 */
	@Input() set hasPermissions(permissions: Array<string | PermissionsObject>) {
		if (permissions == null) {
			this._show();
			return;
		}

		// @note We make sure that permissions array is defined.
		if (! Array.isArray(permissions)) permissions = Array.from(permissions);

		this._permissions = permissions;
		this._permissionService.contextPermissions(this._permissions) // Check permissions of a current context.
			.then((hasPermissions: boolean) => {
				if (hasPermissions) { // If it was previously cleared/disabled, show it again.
					this._show();
				} else { // If access denied, decide whether to hide or disable.
					this._handleAccessDenied();
				}
			}).catch(() => this._handleAccessDenied());
	}

	/**
	 * The mode determines whether to remove the element from DOM or just disable it.
	 * Usage: [hasPermissions]="['READ']" [permissionMode]="'disable'".
	 */
	@Input() set permissionMode(mode: PermissionMode | string) {
		this._mode = mode === 'disable' ? PermissionMode.DISABLE : PermissionMode.REMOVE;
	}

	/**
	 * Creates an instance of the PermissionDirective.
	 *
	 * @constructor
	 * @param {PermissionContextService} _permissionService - The service used to check user permissions.
	 * @param {ViewContainerRef} _viewContainer - The reference to the view container where the template will be embedded.
	 * @param {TemplateRef<any>} _template - The template reference for the content to be conditionally displayed.
	 */
	constructor(
		public _permissionService: PermissionContextService,
		private _viewContainer: ViewContainerRef,
		private _el: ElementRef,
		private _renderer: Renderer2,
		@Optional() private _template: TemplateRef<any>,
	) { }

	private _show() {
		// If we are in REMOVE mode, we need to ensure the view is created.
		if (this._template) {
			// STRUCTURAL MODE (*hasPermissions)
			// If we have a template, we are in "Remove/Hide" mode.
			if (this._viewContainer.length === 0) {
				this._viewContainer.createEmbeddedView(this._template);
			}
		} else {
			// ATTRIBUTE MODE ([hasPermissions])
			// We are modifying the existing element directly.
			this._renderer.setProperty(this._el.nativeElement, 'disabled', false);
			this._renderer.setStyle(this._el.nativeElement, 'pointer-events', 'auto');
			this._renderer.setStyle(this._el.nativeElement, 'opacity', '1');
		}
	}

	private _handleAccessDenied() {
		if (this._template) {
			// STRUCTURAL MODE (*hasPermissions)
			this._viewContainer.clear();
		} else {
			// ATTRIBUTE MODE ([hasPermissions])
			// We target the element itself.
			this._renderer.setProperty(this._el.nativeElement, 'disabled', true);
			this._renderer.setStyle(this._el.nativeElement, 'pointer-events', 'none');
			this._renderer.setStyle(this._el.nativeElement, 'opacity', '0.5');
		}
	}
}
